// frontend/src/admin/Dashboard.js
import React, { useState, useEffect } from 'react';
import { 
  FaTruck, FaBoxOpen, FaChartLine, FaCalendarAlt, FaGlobe, FaSyncAlt,
  FaExclamationTriangle, FaChevronLeft, FaChevronRight, FaUser, FaBox,
  FaClock, FaClipboardList
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axios';
import '../styles/admin.css';
import '../styles/custom.css';

export default function Dashboard() {
  const navigate = useNavigate();
  
  // Stats
  const [stats, setStats] = useState({ 
    todayRegistered: 0, 
    openContainers: 0, 
    todayDistributed: 0,
    totalCargo: 0
  });
  
  // Activity logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 15;

  // Валютын ханш
  const [exchangeRates, setExchangeRates] = useState({
    KRW: { buy: null, sell: null },
    USD: { buy: null, sell: null },
    CNY: { buy: null, sell: null }
  });
  const [activeCurrency, setActiveCurrency] = useState('KRW');
  const [ratesTimestamp, setRatesTimestamp] = useState('');
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
    fetchExchangeRates();
  }, []);

  useEffect(() => {
    fetchActivityLogs();
  }, [currentPage]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/dashboard/stats-v2');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Dashboard stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/activity-logs?page=${currentPage}&limit=${itemsPerPage}`);
      if (response.data.success) {
        setActivityLogs(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Activity logs error:', error);
      setActivityLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchExchangeRates = async () => {
    setRatesLoading(true);
    setRatesError(null);
    try {
      const response = await axiosInstance.get('/api/exchange-rates');
      if (response.data.success) {
        setExchangeRates(response.data.rates);
        setRatesTimestamp(response.data.timestamp);
        if (response.data.error) {
          setRatesError(response.data.error);
        }
      }
    } catch (error) {
      console.error('Exchange rates error:', error);
      setRatesError('Ханш татахад алдаа гарлаа');
      setExchangeRates({
        KRW: { buy: 2.68, sell: 2.68 },
        USD: { buy: 3611, sell: 3611 },
        CNY: { buy: 511.8, sell: 511.8 }
      });
      setRatesTimestamp(new Date().toISOString().split('T')[0]);
    } finally {
      setRatesLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'cargo_registered': <FaTruck className="text-blue-500" />,
      'cargo_distributed': <FaChartLine className="text-green-500" />,
      'cargo_status_changed': <FaSyncAlt className="text-yellow-500" />,
      'container_created': <FaBoxOpen className="text-purple-500" />,
      'container_updated': <FaBox className="text-orange-500" />,
      'user_created': <FaUser className="text-indigo-500" />
    };
    return icons[actionType] || <FaClipboardList className="text-gray-500" />;
  };

  const getActionBadge = (actionType) => {
    const badges = {
      'cargo_registered': { class: 'bg-blue-100 text-blue-700', text: 'Ачаа бүртгэл' },
      'cargo_distributed': { class: 'bg-green-100 text-green-700', text: 'Ачаа тараалт' },
      'cargo_status_changed': { class: 'bg-yellow-100 text-yellow-700', text: 'Төлөв өөрчлөлт' },
      'container_created': { class: 'bg-purple-100 text-purple-700', text: 'Чингэлэг үүсгэсэн' },
      'container_updated': { class: 'bg-orange-100 text-orange-700', text: 'Чингэлэг засвар' },
      'user_created': { class: 'bg-indigo-100 text-indigo-700', text: 'Хэрэглэгч үүсгэсэн' }
    };
    const badge = badges[actionType] || { class: 'bg-gray-100 text-gray-700', text: actionType };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.class}`}>{badge.text}</span>;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('mn-MN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDetailsText = (log) => {
    try {
      const details = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
      if (!details) return '-';
      
      if (log.action_type === 'cargo_registered' || log.action_type === 'cargo_distributed') {
        return `${details.sender_name || '-'} → ${details.receiver_name || '-'}`;
      }
      if (log.action_type === 'container_created' || log.action_type === 'container_updated') {
        const containerStatusNames = {
          'open': 'Нээлттэй',
          'closed': 'Хаалттай',
          'settlement': 'Тооцоо нийлэх',
          'unchanged': 'Өөрчлөлтгүй'
        };
        
        // Хэрэв төлөв өөрчлөгдсөн бол
        if (details.status && details.status !== 'unchanged') {
          return `Төлөв: ${containerStatusNames[details.status] || details.status}`;
        }
        // Хэрэв тараалт огноо өөрчлөгдсөн бол
        if (details.distribution_start_date) {
          return `Тараалт огноо: ${details.distribution_start_date}`;
        }
        return details.name || '-';
      }
      if (log.action_type === 'cargo_status_changed') {
        const statusNames = {
          'registered': 'Бүртгэгдсэн',
          'delivered': 'Хүлээн авсан',
          'distributed': 'Тараагдсан'
        };
        const oldStatusMn = statusNames[details.old_status] || details.old_status || '-';
        const newStatusMn = statusNames[details.new_status] || details.new_status || '-';
        return `${oldStatusMn} → ${newStatusMn}`;
      }
      return '-';
    } catch {
      return '-';
    }
  };

  return (
    <>
      {/* PAGE HEADER */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">Хянах самбар</h1>
            <p className="text-sm text-gray-600">Карго ложистикийн удирдлагын систем</p>
          </div>
          <div className="flex items-center text-xs sm:text-sm text-gray-500 bg-white px-3 py-2 rounded-lg shadow">
            <FaCalendarAlt className="mr-2" />
            {new Date().toLocaleDateString('mn-MN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      {/* STATS CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* ӨНӨӨДӨР БҮРТГЭГДСЭН */}
        <div className="payment-stats-card-v2 revenue-card cursor-pointer" onClick={() => navigate('/admin/cargo')}>
          <div className="flex items-center justify-between mb-2">
            <div className="stats-icon-v2 bg-blue-500">
              <FaTruck className="text-white text-lg" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {loading ? '...' : stats.todayRegistered}
          </div>
          <div className="text-xs text-gray-600 font-medium">Өнөөдөр бүртгэгдсэн</div>
        </div>

        {/* НЭЭЛТТЭЙ ЧИНГЭЛЭГ */}
        <div className="payment-stats-card-v2 expense-card cursor-pointer" onClick={() => navigate('/admin/containers')}>
          <div className="flex items-center justify-between mb-2">
            <div className="stats-icon-v2 bg-green-500">
              <FaBoxOpen className="text-white text-lg" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {loading ? '...' : stats.openContainers}
          </div>
          <div className="text-xs text-gray-600 font-medium">Нээлттэй чингэлэг</div>
        </div>

        {/* ӨНӨӨДӨР ТАРААГДСАН */}
        <div className="payment-stats-card-v2 net-positive-card">
          <div className="flex items-center justify-between mb-2">
            <div className="stats-icon-v2 bg-indigo-500">
              <FaChartLine className="text-white text-lg" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {loading ? '...' : stats.todayDistributed}
          </div>
          <div className="text-xs text-gray-600 font-medium">Өнөөдөр тараагдсан</div>
        </div>

        {/* ВАЛЮТЫН ХАНШ */}
        <div className="payment-stats-card-v2 exchange-card">
          <div className="flex items-center justify-between mb-2">
            <div className="stats-icon-v2 bg-purple-500">
              <FaGlobe className="text-white text-base" />
            </div>
            <button 
              onClick={fetchExchangeRates}
              className="text-purple-600 hover:text-purple-800 p-1 rounded-full hover:bg-purple-100 transition-colors"
              title="Ханш шинэчлэх"
              disabled={ratesLoading}
            >
              <FaSyncAlt className={`text-xs ${ratesLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {ratesLoading ? (
            <div className="text-center py-4">
              <div className="loading-spinner mx-auto mb-2"></div>
              <div className="text-xs text-gray-500">Татаж байна...</div>
            </div>
          ) : (
            <>
              <div className="flex gap-1 mb-2 border-b border-purple-200">
                {['KRW', 'USD', 'CNY'].map(currency => (
                  <button
                    key={currency}
                    onClick={() => setActiveCurrency(currency)}
                    className={`flex-1 text-xs font-bold py-1 rounded-t transition-colors ${
                      activeCurrency === currency ? 'bg-purple-500 text-white' : 'text-purple-600 hover:bg-purple-100'
                    }`}
                  >
                    {currency}
                  </button>
                ))}
              </div>
              <div className="space-y-1 mb-2">
                <div className="flex justify-between items-center text-xs py-1">
                  <span className="font-semibold text-purple-700">Ханш:</span>
                  <span className="font-bold text-gray-900">
                    {exchangeRates?.[activeCurrency]?.sell 
                      ? `${exchangeRates[activeCurrency].sell.toLocaleString()} ₮` : '-'}
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-500 text-center border-t border-purple-200 pt-1.5 space-y-0.5">
                <div className="font-semibold text-purple-700">Хаан банк бэлэн зарах</div>
                <div className="text-gray-600">
                  {ratesTimestamp ? new Date(ratesTimestamp).toLocaleDateString('mn-MN') : '-'}
                </div>
              </div>
              {ratesError && (
                <div className="text-xs text-orange-600 text-center mt-1 flex items-center justify-center gap-1">
                  <FaExclamationTriangle className="text-[10px]" />
                  <span>Approximate</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ACTIVITY LOGS TABLE */}
      <div className="table-container bg-white rounded-lg shadow-lg">
        <div className="table-header px-4 py-4 border-b flex justify-between items-center">
          <h2 className="table-title text-lg md:text-xl flex items-center gap-2">
            <FaClipboardList className="text-blue-500" />
            Сүүлийн үйлдлүүд
          </h2>
          <button 
            onClick={() => { fetchActivityLogs(); fetchDashboardStats(); }}
            className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-100 transition-colors"
            title="Шинэчлэх"
          >
            <FaSyncAlt className={logsLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left">Үйлдэл</th>
                <th className="px-4 py-3 text-left">Код</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Дэлгэрэнгүй</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Хэрэглэгч</th>
                <th className="px-4 py-3 text-left">Огноо</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-8">
                    <div className="loading-spinner mx-auto"></div>
                    <p className="mt-2 text-gray-500">Ачааллаж байна...</p>
                  </td>
                </tr>
              ) : activityLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    Үйлдэл олдсонгүй
                  </td>
                </tr>
              ) : (
                activityLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getActionIcon(log.action_type)}
                        {getActionBadge(log.action_type)}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-blue-600">
                      {log.entity_code || '-'}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-sm text-gray-600">
                      {getDetailsText(log)}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center">
                          <FaUser className="text-gray-500 text-xs" />
                        </div>
                        <span className="text-sm">{log.user_full_name || log.username || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <FaClock className="text-gray-400" />
                        {formatDateTime(log.created_at)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="table-footer px-4 py-3 bg-gray-50 border-t">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <span className="text-sm text-gray-600">
              Хуудас {currentPage} / {totalPages}
            </span>
            
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 rounded border text-sm flex items-center gap-1 ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  }`}
                >
                  <FaChevronLeft className="text-xs" />
                  Өмнөх
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 rounded text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 rounded border text-sm flex items-center gap-1 ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  }`}
                >
                  Дараах
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}